import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Textarea from '../components/ui/Textarea';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import AddressBookModal from '../components/address-book/AddressBookModal';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import Input from '../components/ui/Input';
import SearchableCombobox from '../components/ui/SearchableCombobox';
import { CheckSquare, User, AlertCircle, FolderOpen, FileText, Truck, ExternalLink, Hash, Calendar, Printer, Download, Plus, ArrowRight, Clock3, MessageSquareText, BellRing, SlidersHorizontal, ChevronUp } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { fetchAllPaginatedRows } from '../utils/paginatedExport';
import { useAuth } from '../context/AuthContext';
import { useUIStore } from '../store/useUIStore';

const collapseDuplicateCaseRows = (rows = []) => {
  const latestByKey = new Map();
  const keyRegistry = new Map();

  rows.forEach((row) => {
    const rowType = String(row?.type || row?.letterType || 'receipt').trim().toLowerCase();
    const diaryNumber = String(
      row?.diaryNumber ||
      row?.dakNumber ||
      row?.receiptNumber ||
      row?.dispatchNumber ||
      row?.fileNumber ||
      ''
    ).trim();
    const subject = String(row?.actionRequired || row?.subject || '').trim().toLowerCase();
    const sender = String(row?.sender || row?.assignedToUser?.name || row?.forwardedToName || row?.recipient || '').trim().toLowerCase();
    const dateKey = String(row?.dateReceived || row?.createdAt || row?.updatedAt || '').slice(0, 10);
    const diaryKey = diaryNumber ? `${rowType}::diary::${diaryNumber.toLowerCase()}` : null;
    const fallbackKey = `${rowType}::fallback::${subject || 'no-subject'}::${sender || 'no-sender'}::${dateKey || 'no-date'}`;
    const candidateKeys = diaryKey ? [diaryKey, fallbackKey] : [fallbackKey];

    const mappedCanonicalKey = candidateKeys.map((key) => keyRegistry.get(key)).find((key) => key !== undefined);
    const canonicalKey = mappedCanonicalKey || candidateKeys[0];

    for (const key of candidateKeys) {
      keyRegistry.set(key, canonicalKey);
    }

    const current = latestByKey.get(canonicalKey);
    if (!current) {
      latestByKey.set(canonicalKey, row);
      return;
    }

    const currentDate = new Date(current?.updatedAt || current?.createdAt || 0).getTime();
    const incomingDate = new Date(row?.updatedAt || row?.createdAt || 0).getTime();

    if (incomingDate > currentDate) {
      latestByKey.set(canonicalKey, row);
    }
  });

  return Array.from(latestByKey.values());
};

const ComplianceList = () => {
  const navigate = useNavigate();
  const [compliances, setCompliances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [efileAnalytics, setEfileAnalytics] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [visibleComplianceColumns, setVisibleComplianceColumns] = useState([]);
  const [tableComplianceColumnWidths, setTableComplianceColumnWidths] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [columnFilters, setColumnFilters] = useState({
    referenceType: '',
    dakNumber: '',
    date: '',
    actionRequired: '',
    assignedTo: '',
    status: ''
  });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailRecipients, setDetailRecipients] = useState([]);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [editFields, setEditFields] = useState({
    forwardedTo: '',
    forwardedToName: '',
    forwardedAt: '',
    comments: '',
    officerName: '',
    officeDiaryNumber: '',
    officeDiaryDate: ''
  });
  const [showFurtherCompliance, setShowFurtherCompliance] = useState(false);
  const { get, put, post } = useApi();
  const { settings, loading: settingsLoading } = useSettings();
  const { tableDensity, setTableDensity } = useUIStore();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const { user } = useAuth();
  const isEditable = ['receipt', 'dispatch'].includes(String(selectedCompliance?.type || ''));
  const modalSteps = useMemo(() => {
    const t = String(selectedCompliance?.type || '');
    if (t === 'receipt') {
      return [
        { key: 'forward', label: 'Forward to Officer' },
        { key: 'comments', label: 'Remarks' }
      ];
    }
    if (t === 'dispatch') {
      return [{ key: 'comments', label: 'Remarks' }];
    }
    return [];
  }, [selectedCompliance?.type]);
  const modalStepKey = modalSteps[modalStep]?.key || 'comments';

  const loadForwardRecipients = async () => {
    try {
      const response = await get('/address-book?includeUsers=1');
      const rows = Array.isArray(response?.data) ? response.data : [];
      const seen = new Set();
      const dedupedRows = rows.filter(Boolean).filter((row) => {
        const key = `${row.sourceType || 'contact'}:${row.sourceId ?? row.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (dedupedRows.length > 0) return dedupedRows;

      const usersResponse = await get('/users?limit=200');
      const userRows = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
      return userRows.map((user) => ({
        id: user.id,
        sourceType: 'user',
        sourceId: user.id,
        linkedUserId: user.id,
        displayName: user.name,
        organizationName: user.name,
        contactPerson: user.name,
        designation: user.designation || user.role || '',
        address: user.officialAddress || '',
        email: user.email || '',
        phoneNumber: user.phone || '',
        role: user.role,
        isLinkedUser: true
      }));
    } catch {
      try {
        const usersResponse = await get('/users?limit=200');
        const userRows = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
        return userRows.map((user) => ({
          id: user.id,
          sourceType: 'user',
          sourceId: user.id,
          linkedUserId: user.id,
          displayName: user.name,
          organizationName: user.name,
          contactPerson: user.name,
          designation: user.designation || user.role || '',
          address: user.officialAddress || '',
          email: user.email || '',
          phoneNumber: user.phone || '',
          role: user.role,
          isLinkedUser: true
        }));
      } catch {
        return [];
      }
    }
  };

  const buildRecipientItems = (rows) => (Array.isArray(rows) ? rows : []).map((row) => {
    const isLinkedUser = String(row?.sourceType || '') === 'user' || row?.linkedUserId !== null && row?.linkedUserId !== undefined;
    const recipientId = isLinkedUser ? (row.linkedUserId ?? row.sourceId ?? row.id) : (row.sourceId ?? row.id);
    const displayName = row.displayName || row.organizationName || row.contactPerson || row.name || '';
    const address = row.address || row.officialAddress || '';
    const descriptionParts = [];
    if (isLinkedUser && recipientId !== undefined && recipientId !== null) descriptionParts.push(`User ID: ${recipientId}`);
    if (!isLinkedUser) descriptionParts.push('Address Book');
    if (address) descriptionParts.push(address);
    if (row.email) descriptionParts.push(row.email);
    return {
      value: `${isLinkedUser ? 'user' : 'contact'}:${recipientId}`,
      label: displayName,
      description: descriptionParts.join(' • '),
      searchText: [recipientId, displayName, address, row.email, row.contactPerson, row.organizationName, row.designation, row.role]
        .filter(Boolean)
        .join(' '),
      sourceType: row.sourceType,
      linkedUserId: isLinkedUser ? String(recipientId) : '',
      forwardedToName: displayName
    };
  });

  const getSelectedRecipientValue = (rows) => {
    if (editFields.forwardedTo) {
      const match = (Array.isArray(rows) ? rows : []).find((row) => String(row.linkedUserId ?? row.sourceId ?? row.id) === String(editFields.forwardedTo));
      if (match) return `${String(match.sourceType || 'user') === 'user' || match.linkedUserId != null ? 'user' : 'contact'}:${match.linkedUserId ?? match.sourceId ?? match.id}`;
      return `user:${editFields.forwardedTo}`;
    }
    if (String(editFields.forwardedToName || '').trim()) {
      const target = String(editFields.forwardedToName).trim().toLowerCase();
      const match = (Array.isArray(rows) ? rows : []).find((row) => {
        const name = String(row.displayName || row.organizationName || row.contactPerson || row.name || '').trim().toLowerCase();
        return name && name === target;
      });
      if (match) {
        const recipientId = match.linkedUserId ?? match.sourceId ?? match.id;
        const isLinkedUser = String(match.sourceType || '') === 'user' || match.linkedUserId != null;
        return `${isLinkedUser ? 'user' : 'contact'}:${recipientId}`;
      }
    }
    return '';
  };

  const handleRecipientChange = (value) => {
    const recipient = detailRecipients.find((row) => {
      const isLinkedUser = String(row?.sourceType || '') === 'user' || row?.linkedUserId !== null && row?.linkedUserId !== undefined;
      const recipientId = isLinkedUser ? (row.linkedUserId ?? row.sourceId ?? row.id) : (row.sourceId ?? row.id);
      return `${isLinkedUser ? 'user' : 'contact'}:${recipientId}` === value;
    });

    if (!recipient) {
      setEditFields((prev) => ({ ...prev, forwardedTo: '', forwardedToName: '' }));
      return;
    }

    const isLinkedUser = String(recipient?.sourceType || '') === 'user' || recipient?.linkedUserId !== null && recipient?.linkedUserId !== undefined;
    const recipientName = recipient.displayName || recipient.organizationName || recipient.contactPerson || recipient.name || '';
    setEditFields((prev) => ({
      ...prev,
      forwardedTo: isLinkedUser ? String(recipient.linkedUserId ?? recipient.sourceId ?? recipient.id) : '',
      forwardedToName: isLinkedUser ? '' : recipientName
    }));
  };

  const handleContactAdded = (contact) => {
    if (!contact?.id) return;
    const displayName = contact.contactPerson || contact.organizationName || contact.email || '';
    const newRecipient = {
      ...contact,
      sourceType: 'address_book',
      sourceId: contact.id,
      displayName
    };
    setDetailRecipients((current) => [
      ...current.filter((row) => String(row.sourceId ?? row.id) !== String(contact.id)),
      newRecipient
    ]);
    setEditFields((current) => ({
      ...current,
      forwardedTo: '',
      forwardedToName: displayName
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCompliances = useCallback(async () => {
    setLoading(true);
    try {
      // Map frontend sort keys to backend keys
      let sortKey = sortConfig.key;
      if (sortKey === 'referenceType') sortKey = 'type';
      if (sortKey === 'assignedToUser') sortKey = 'assignedToId';
      
      const response = await get(`/compliance?page=${page}&limit=${limit}&search=${debouncedSearch}&sortBy=${sortKey}&order=${sortConfig.direction}`);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const filteredRows = (isEfileEnabled ? rows : rows.filter(r => String(r?.type || '').toLowerCase() !== 'efile'));
      setCompliances(collapseDuplicateCaseRows(filteredRows));
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch compliances', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [get, page, limit, debouncedSearch, isEfileEnabled, sortConfig]);

  useEffect(() => {
    fetchCompliances();
  }, [fetchCompliances]);

  useEffect(() => {
    if (isEfileEnabled) return;
    setCompliances(prev => (Array.isArray(prev) ? prev.filter(r => String(r?.type || '').toLowerCase() !== 'efile') : []));
  }, [isEfileEnabled]);

  useEffect(() => {
    if (settingsLoading || !isEfileEnabled) {
      setEfileAnalytics(null);
      return;
    }

    (async () => {
      try {
        const response = await get('/efile/analytics');
        setEfileAnalytics(response.data || null);
      } catch (error) {
        setEfileAnalytics(null);
      }
    })();
  }, [get, isEfileEnabled, settingsLoading]);

  useEffect(() => {
    (async () => {
      try {
        const response = await get('/dashboard/stats');
        setDashboardStats(response.data || null);
      } catch (error) {
        setDashboardStats(null);
      }
    })();
  }, [get]);
  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const refreshComplianceHistory = useCallback(async (compliance) => {
    if (!compliance) {
      setDetailHistory([]);
      return [];
    }

    try {
      const res = await get(`/compliance/logs?letterType=${encodeURIComponent(compliance.type)}&letterId=${encodeURIComponent(compliance.id)}`);
      const nextHistory = Array.isArray(res?.data) ? res.data : [];
      setDetailHistory(nextHistory);
      return nextHistory;
    } catch (error) {
      console.error('Failed to fetch compliance history', error);
      setDetailHistory([]);
      return [];
    }
  }, [get]);

  const handleQuickStatusChange = async (complianceRow, nextStatus) => {
    const activeCompliance = typeof complianceRow === 'string' ? selectedCompliance : complianceRow;
    const finalStatus = typeof complianceRow === 'string' ? complianceRow : nextStatus;
    if (!activeCompliance || !finalStatus) return;

    const normalizedStatus = String(finalStatus || '').trim();
    const resolvedStatus = normalizedStatus === 'Complete' ? 'Completed' : normalizedStatus === 'Close' ? 'Closed' : normalizedStatus;
    await handleUpdateStatus(resolvedStatus, { closeAfter: false }, activeCompliance);
  };

  const handleUpdateStatus = async (status, options = {}, complianceOverride = null) => {
    const activeCompliance = complianceOverride || selectedCompliance;
    if (!activeCompliance) return;
    try {
      const closeAfter = options?.closeAfter !== false;
      const payload = { status };
      if (activeCompliance.type === 'receipt') {
        const hasForwardTarget = Boolean(editFields.forwardedTo || editFields.forwardedToName);
        payload.forwardedTo = editFields.forwardedTo ? editFields.forwardedTo : null;
        payload.forwardedToName = editFields.forwardedTo ? '' : (editFields.forwardedToName || '');
        payload.forwardedAt = hasForwardTarget ? (editFields.forwardedAt || null) : null;
        payload.comments = editFields.comments || '';
      } else if (activeCompliance.type === 'dispatch') {
        payload.comments = editFields.comments || '';
      } else {
        toast.error('This item type cannot be updated from Compliance');
        return;
      }

      await put(`/compliance/${activeCompliance.id}?type=${activeCompliance.type}`, payload);
      toast.success(`Compliance marked as ${status}`);
      await fetchCompliances();
      setSelectedCompliance(prev => (prev ? { ...prev, status } : { ...activeCompliance, status }));
      if (activeCompliance.type === 'receipt') {
        const receiptRes = await get(`/receipt/${activeCompliance.id}`);
        setDetail(receiptRes?.data || null);
      } else if (activeCompliance.type === 'dispatch') {
        const dispatchRes = await get(`/dispatch/${activeCompliance.id}`);
        setDetail(dispatchRes?.data || null);
      }
      await refreshComplianceHistory(activeCompliance);
      if (closeAfter) {
        setIsModalOpen(false);
        setDetail(null);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openUpdateModal = async (compliance) => {
    setSelectedCompliance(compliance);
    setIsModalOpen(true);
    setShowFurtherCompliance(false);
    setDetail(null);
    setModalStep(0);
    setDetailLoading(true);

    try {
      if (compliance.type === 'receipt') {
        const [receiptRes, recipients] = await Promise.all([
          get(`/receipt/${compliance.id}`),
          loadForwardRecipients()
        ]);
        setDetail(receiptRes?.data || null);
        setDetailRecipients(recipients);
        const previousHistory = await refreshComplianceHistory(compliance);
        const previousLog = Array.isArray(previousHistory) && previousHistory.length > 0 ? previousHistory[0] : null;

        setEditFields({
          forwardedTo: receiptRes?.data?.forwardedTo || '',
          forwardedToName: receiptRes?.data?.forwardedToName || '',
          forwardedAt: receiptRes?.data?.forwardedAt || '',
          comments: receiptRes?.data?.remarks || '',
          officerName: previousLog?.officerName || receiptRes?.data?.lastComplianceOfficerName || receiptRes?.data?.forwardedToName || receiptRes?.data?.forwardedTo || '',
          officeDiaryNumber: previousLog?.diaryNumber || receiptRes?.data?.lastComplianceDiaryNumber || '',
          officeDiaryDate: previousLog?.diaryDate || receiptRes?.data?.lastComplianceDiaryDate || ''
        });
      } else if (compliance.type === 'dispatch') {
        const dispatchRes = await get(`/dispatch/${compliance.id}`);
        setDetail(dispatchRes?.data || null);
        setDetailRecipients([]);
        const previousHistory = await refreshComplianceHistory(compliance);
        const previousLog = Array.isArray(previousHistory) && previousHistory.length > 0 ? previousHistory[0] : null;
        setEditFields({
          forwardedTo: '',
          forwardedToName: '',
          forwardedAt: '',
          comments: dispatchRes?.data?.remarks || '',
          officerName: previousLog?.officerName || '',
          officeDiaryNumber: previousLog?.diaryNumber || '',
          officeDiaryDate: previousLog?.diaryDate || ''
        });
      } else {
        setDetail(null);
        setDetailRecipients([]);
        setDetailHistory([]);
        setEditFields({
          forwardedTo: '',
          forwardedToName: '',
          forwardedAt: '',
          comments: '',
          officerName: '',
          officeDiaryNumber: '',
          officeDiaryDate: ''
        });
      }
    } catch (e) {
      void e;
      toast.error('Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedCompliance) return;
    if (!['receipt', 'dispatch'].includes(selectedCompliance.type)) return;
    if (selectedCompliance.type === 'receipt' && modalStepKey === 'forward' && !editFields.forwardedTo && !String(editFields.forwardedToName || '').trim() && !editFields.forwardedAt) {
      const hasExistingForward = Boolean(detail?.forwardedTo || detail?.forwardedToName || detail?.forwardedAt);
      if (hasExistingForward) {
        // Allow clearing a mistaken assignment intentionally
      } else {
        // Forward target is optional for a remarks-only compliance update.
      }
    }
    let status = detail?.status || selectedCompliance.status || 'In Progress';
    if (status === 'Pending') status = 'In Progress';
    await handleUpdateStatus(status, { closeAfter: true });
  };

  const latestComplianceHistory = useMemo(() => {
    if (!Array.isArray(detailHistory) || detailHistory.length === 0) return null;
    return detailHistory[0];
  }, [detailHistory]);

  const getSelectedFurtherOfficerValue = () => {
    const targetName = String(editFields.officerName || '').trim().toLowerCase();
    if (!targetName) return '';
    const match = (Array.isArray(detailRecipients) ? detailRecipients : []).find((row) => {
      const name = String(row.displayName || row.organizationName || row.contactPerson || row.name || '').trim().toLowerCase();
      return name && name === targetName;
    });
    if (!match) return '';
    const recipientId = match.linkedUserId ?? match.sourceId ?? match.id;
    const isLinkedUser = String(match.sourceType || '') === 'user' || match.linkedUserId != null;
    return `${isLinkedUser ? 'user' : 'contact'}:${recipientId}`;
  };

  const handleFurtherComplianceOfficerSelect = (value) => {
    const recipient = detailRecipients.find((row) => {
      const isLinkedUser = String(row?.sourceType || '') === 'user' || row?.linkedUserId !== null && row?.linkedUserId !== undefined;
      const recipientId = isLinkedUser ? (row.linkedUserId ?? row.sourceId ?? row.id) : (row.sourceId ?? row.id);
      return `${isLinkedUser ? 'user' : 'contact'}:${recipientId}` === value;
    });

    if (!recipient) {
      setEditFields((prev) => ({ ...prev, officerName: '' }));
      return;
    }

    const officerName = recipient.displayName || recipient.organizationName || recipient.contactPerson || recipient.name || '';
    setEditFields((prev) => ({ ...prev, officerName }));
  };

  const handleFurtherCompliance = async () => {
    if (!selectedCompliance) return;
    const diaryNumber = String(editFields.officeDiaryNumber || '').trim();
    const diaryDate = String(editFields.officeDiaryDate || '').trim();
    const officerName = String(editFields.officerName || detail?.forwardedToName || detail?.lastComplianceOfficerName || selectedCompliance?.assignedToUser?.name || '').trim();

    if (!officerName) {
      toast.error('Please select or type the officer to whom the file is being returned.');
      return;
    }

    try {
      const payload = {
        letterType: selectedCompliance.type,
        letterId: selectedCompliance.id,
        actionTaken: 'Further compliance',
        status: 'In Progress',
        remarks: editFields.comments || 'Further compliance entered',
        officerName,
        diaryNumber,
        diaryDate
      };

      await post('/compliance', payload);
      toast.success('Further compliance recorded successfully');
      setShowFurtherCompliance(false);
      setIsModalOpen(false);
      setSelectedCompliance(null);
      setDetail(null);
      setDetailHistory([]);
      await fetchCompliances();
    } catch (error) {
      console.error('Failed to save further compliance', error);
      toast.error('Failed to save further compliance');
    }
  };

  const assignedDateValue = selectedCompliance?.forwardedAt || detail?.forwardedAt || detail?.updatedAt || selectedCompliance?.updatedAt || detail?.createdAt || selectedCompliance?.createdAt;

  const handleOpenCase = async () => {
    if (!selectedCompliance) return;
    const t = String(selectedCompliance.type || '');
    if (t === 'receipt') navigate(`/diary/${selectedCompliance.id}/view`);
    else if (t === 'dispatch') navigate(`/dispatch/${selectedCompliance.id}/view`);
  };

  const getComplianceDateValue = (row) => row?.date || row?.dateReceived || row?.dateIssued || row?.createdAt || row?.updatedAt || '';

  const getVisibleColumns = (list = []) => {
    const key = (column) => String(column.header || column.accessorKey || '').trim();
    return list.filter((column) => {
      const match = visibleComplianceColumns.find((item) => item.key === key(column));
      return match ? match.visible : column.visible !== false;
    });
  };

  const getExportColumnWidths = (exportColumns = []) => {
    const widths = {};
    exportColumns.forEach((column, index) => {
      const key = String(column.header || column.accessorKey || '').trim();
      const width = Number(tableComplianceColumnWidths[key] || 0);
      if (width > 0) {
        widths[index] = { wch: Math.min(Math.max(width / 8, 12), 40) };
      }
    });
    return widths;
  };

  const handleExportPDF = async () => {
    try {
      const exportColumns = [
        { header: 'Ref Type', accessorKey: 'referenceType' },
        { header: 'Diary/Dispatch No.', accessorKey: 'dakNumber' },
        { header: 'Date', accessorKey: (row) => formatDate(getComplianceDateValue(row)) },
        { header: 'Action Required', accessorKey: 'actionRequired' },
        { header: 'Assigned To', accessorKey: 'assignedToUser.name' },
        { header: 'Status', accessorKey: 'status' }
      ];

      const rows = await fetchAllCompliancesForExport();
      await exportToPDF(rows, getVisibleColumns(exportColumns), 'Compliance Register', {
        orientation: 'landscape',
        officeName: user?.officeName || '',
        columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
      });
      toast.success('PDF exported successfully');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintPDF = async () => {
    try {
      const exportColumns = [
        { header: 'Ref Type', accessorKey: 'referenceType' },
        { header: 'Diary/Dispatch No.', accessorKey: 'dakNumber' },
        { header: 'Date', accessorKey: (row) => formatDate(getComplianceDateValue(row)) },
        { header: 'Action Required', accessorKey: 'actionRequired' },
        { header: 'Assigned To', accessorKey: 'assignedToUser.name' },
        { header: 'Status', accessorKey: 'status' }
      ];

      const rows = await fetchAllCompliancesForExport();
      const result = await exportToPDF(rows, getVisibleColumns(exportColumns), 'Compliance Register', {
        orientation: 'landscape',
        officeName: user?.officeName || '',
        output: 'print',
        columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
      });
      if (result?.mode === 'print-fallback-download') {
        toast.success('Popup blocked. PDF downloaded instead.');
      } else {
        toast.success('Print preview opened');
      }
    } catch {
      toast.error('Failed to print');
    }
  };

  const handleExportExcel = async () => {
    const allRows = await fetchAllCompliancesForExport();
    const exportColumns = [
      { header: 'Ref Type', accessorKey: 'referenceType' },
      { header: 'Diary/Dispatch No.', accessorKey: 'dakNumber' },
      { header: 'Date', accessorKey: (row) => formatDate(getComplianceDateValue(row)) },
      { header: 'Action Required', accessorKey: 'actionRequired' },
      { header: 'Assigned To', accessorKey: 'assignedToUser.name' },
      { header: 'Status', accessorKey: 'status' }
    ];
    const rows = allRows.map((r) => Object.fromEntries(getVisibleColumns(exportColumns).map((column) => {
      const value = typeof column.accessorKey === 'function' ? column.accessorKey(r) : (r[column.accessorKey] ?? '');
      return [column.header, value];
    })));
    exportToExcel(rows, 'Compliance_Register', {
      columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
    });
    toast.success('Excel exported successfully');
  };

  const columns = [
    {
      header: 'Ref Type',
      accessorKey: 'referenceType',
      sortable: true,
      className: 'w-28',
      cellClassName: 'w-28',
      render: (row) => (
        <span className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
          {row.referenceType || 'N/A'}
        </span>
      )
    },
    {
      header: 'Diary/Dispatch No.',
      accessorKey: 'dakNumber',
      sortable: true,
      className: 'w-44',
      cellClassName: 'w-44',
      render: (row) => (
        <span className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 font-semibold text-gray-800 shadow-sm">
          {row.dakNumber || '-'}
        </span>
      )
    },
    {
      header: 'Date',
      accessorKey: 'date',
      sortable: true,
      className: 'w-32',
      cellClassName: 'w-32',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {formatDate(getComplianceDateValue(row)) || '-'}
        </span>
      )
    },
    {
      header: 'Action Required',
      accessorKey: 'actionRequired',
      sortable: true,
      className: 'w-[18rem] sm:w-[22rem] lg:w-[26rem] xl:w-[30rem]',
      cellClassName: 'w-[18rem] sm:w-[22rem] lg:w-[26rem] xl:w-[30rem]',
      noWrap: false,
      render: (row) => (
        <div className="flex items-start" title={row.actionRequired}>
          <AlertCircle className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <span className="block leading-snug break-words [overflow-wrap:anywhere]">
            {row.actionRequired || '-'}
          </span>
        </div>
      )
    },
    {
      header: 'Assigned To',
      accessorKey: 'assignedToUser',
      sortable: true,
      className: 'hidden lg:table-cell w-44',
      cellClassName: 'hidden lg:table-cell w-44',
      render: (row) => (
        <div className="flex items-center text-gray-600">
          <User className="w-4 h-4 mr-1" />
          {row.assignedToUser?.name || '-'}
        </div>
      )
    },
    {
      header: 'Status',
      accessorKey: 'status',
      sortable: true,
      className: 'w-32',
      cellClassName: 'w-32',
      render: (row) => {
        const status = row.status || 'In Progress';
        const palette =
          status === 'Completed'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : status === 'Pending'
              ? 'border-rose-200 bg-rose-50 text-rose-700'
              : 'border-indigo-200 bg-indigo-50 text-indigo-700';

        return (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm ${palette}`}>
            {status}
          </span>
        );
      }
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      className: 'w-40',
      cellClassName: 'w-40',
      render: (row) => (
        <div className="flex flex-col gap-2 min-w-[140px]">
          <button
            type="button"
            onClick={() => openUpdateModal(row)}
            className="inline-flex items-center justify-start rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11px] font-medium text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
          >
            <CheckSquare className="mr-1.5 h-3.5 w-3.5" /> Update
          </button>
          <button
            type="button"
            onClick={() => handleQuickStatusChange(row, 'Completed')}
            className="inline-flex items-center justify-start rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-medium text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
          >
            <CheckSquare className="mr-1.5 h-3.5 w-3.5" /> Complete
          </button>
          <button
            type="button"
            onClick={async () => { await openUpdateModal(row); setShowFurtherCompliance(true); }}
            className="inline-flex items-center justify-start rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-700 transition hover:border-amber-300 hover:bg-amber-100"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Further
          </button>
        </div>
      )
    },
  ];

  const complianceStats = useMemo(() => {
    const rows = Array.isArray(compliances) ? compliances : [];
    const total = rows.length;
    const inProgress = rows.filter((row) => String(row?.status || '').toLowerCase() === 'in progress').length;
    const completed = rows.filter((row) => String(row?.status || '').toLowerCase() === 'completed').length;
    const pending = rows.filter((row) => String(row?.status || '').toLowerCase() === 'pending').length;
    const thisWeek = rows.filter((row) => {
      const updatedAt = row?.updatedAt || row?.createdAt;
      if (!updatedAt) return false;
      const time = new Date(updatedAt).getTime();
      const diffDays = (Date.now() - time) / (1000 * 60 * 60 * 24);
      return diffDays <= 7;
    }).length;

    return { total, inProgress, completed, pending, thisWeek };
  }, [compliances]);

  const filteredRows = (Array.isArray(compliances) ? compliances : [])
    .filter(r => {
      if (typeFilter === 'all') return true;
      if (typeFilter === 'efile') return String(r?.type || '').toLowerCase() === 'efile';
      if (typeFilter === 'receipt') return String(r?.type || '').toLowerCase() === 'receipt';
      if (typeFilter === 'dispatch') return String(r?.type || '').toLowerCase() === 'dispatch';
      return true;
    })
    .filter(r => {
      if (statusFilter !== 'all' && String(r?.status || '').toLowerCase() !== statusFilter.toLowerCase()) return false;
      return true;
    })
    .filter(r => {
      const refOk = !columnFilters.referenceType || String(r?.referenceType || '').toLowerCase().includes(String(columnFilters.referenceType).toLowerCase());
      const dakOk = !columnFilters.dakNumber || String(r?.dakNumber || '').toLowerCase().includes(String(columnFilters.dakNumber).toLowerCase());
      const dateOk = !columnFilters.date || String(getComplianceDateValue(r) || '').toLowerCase().includes(String(columnFilters.date).toLowerCase());
      const actionOk = !columnFilters.actionRequired || String(r?.actionRequired || '').toLowerCase().includes(String(columnFilters.actionRequired).toLowerCase());
      const assignedOk = !columnFilters.assignedTo || String(r?.assignedToUser?.name || '').toLowerCase().includes(String(columnFilters.assignedTo).toLowerCase());
      const statusOk = !columnFilters.status || String(r?.status || '').toLowerCase().includes(String(columnFilters.status).toLowerCase());
      return refOk && dakOk && dateOk && actionOk && assignedOk && statusOk;
    });

  const applyComplianceFilters = (rows) => (Array.isArray(rows) ? rows : [])
    .filter(r => {
      if (typeFilter === 'all') return true;
      if (typeFilter === 'efile') return String(r?.type || '').toLowerCase() === 'efile';
      if (typeFilter === 'receipt') return String(r?.type || '').toLowerCase() === 'receipt';
      if (typeFilter === 'dispatch') return String(r?.type || '').toLowerCase() === 'dispatch';
      return true;
    })
    .filter(r => {
      if (statusFilter !== 'all' && String(r?.status || '').toLowerCase() !== statusFilter.toLowerCase()) return false;
      return true;
    })
    .filter(r => {
      const refOk = !columnFilters.referenceType || String(r?.referenceType || '').toLowerCase().includes(String(columnFilters.referenceType).toLowerCase());
      const dakOk = !columnFilters.dakNumber || String(r?.dakNumber || '').toLowerCase().includes(String(columnFilters.dakNumber).toLowerCase());
      const dateOk = !columnFilters.date || String(getComplianceDateValue(r) || '').toLowerCase().includes(String(columnFilters.date).toLowerCase());
      const actionOk = !columnFilters.actionRequired || String(r?.actionRequired || '').toLowerCase().includes(String(columnFilters.actionRequired).toLowerCase());
      const assignedOk = !columnFilters.assignedTo || String(r?.assignedToUser?.name || '').toLowerCase().includes(String(columnFilters.assignedTo).toLowerCase());
      const statusOk = !columnFilters.status || String(r?.status || '').toLowerCase().includes(String(columnFilters.status).toLowerCase());
      return refOk && dakOk && dateOk && actionOk && assignedOk && statusOk;
    });

  const fetchAllCompliancesForExport = async () => {
    let sortKey = sortConfig.key;
    if (sortKey === 'referenceType') sortKey = 'type';
    if (sortKey === 'assignedToUser') sortKey = 'assignedToId';

    const allRows = await fetchAllPaginatedRows({
      fetchPage: (currentPage) => get(`/compliance?page=${currentPage}&limit=200&search=${debouncedSearch}&sortBy=${sortKey}&order=${sortConfig.direction}`),
      transformRows: (rows) => {
        const filtered = (isEfileEnabled ? rows : rows.filter(r => String(r?.type || '').toLowerCase() !== 'efile'));
        return collapseDuplicateCaseRows(filtered);
      }
    });

    return applyComplianceFilters(allRows);
  };

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Tracker</h1>
          <p className="text-sm text-gray-500">Monitor action items and status</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <Button
              size="sm"
              variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('comfortable')}
            >
              Comfortable
            </Button>
            <Button
              size="sm"
              variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('compact')}
            >
              Compact
            </Button>
          </div>
          <Button variant="secondary" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2 text-red-500" /> PDF
          </Button>
          <Button variant="secondary" onClick={handlePrintPDF}>
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="secondary" onClick={handleExportExcel}>
            <Download className="w-4 h-4 mr-2 text-green-600" /> Excel
          </Button>
        </div>
      </div>

      {(dashboardStats || (isEfileEnabled && efileAnalytics)) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {dashboardStats && (
            <>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Dispatches</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats?.counts?.dispatches || 0}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Diaries</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats?.counts?.receipts || 0}</p>
                </div>
              </div>
            </>
          )}
          {isEfileEnabled && efileAnalytics && (
            <>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Active e-Files</p>
                  <p className="text-2xl font-bold text-gray-900">{efileAnalytics?.files?.active || 0}</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600">
                  <FolderOpen className="w-5 h-5" />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Notings Today</p>
                  <p className="text-2xl font-bold text-gray-900">{efileAnalytics?.notings?.today || 0}</p>
                </div>
                <div className="p-3 bg-cyan-50 rounded-lg text-cyan-600">
                  <FileText className="w-5 h-5" />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Closed e-Files</p>
                  <p className="text-2xl font-bold text-gray-900">{efileAnalytics?.files?.closed || 0}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600">
                  <CheckSquare className="w-5 h-5" />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">e-File Movements</p>
                  <p className="text-2xl font-bold text-gray-900">{efileAnalytics?.movementsToday || 0}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-orange-600">
                  <Truck className="w-5 h-5" />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow mb-6 p-3 sm:p-4">
        <SearchInput 
          value={search} 
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search by diary/dispatch no, subject, sender, or status..." 
        />
      </div>

      <div className="bg-white rounded-lg shadow mb-6 p-3 sm:p-4">
        <div className="flex justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMoreFilters(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
          >
            {showMoreFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <SlidersHorizontal className="h-3.5 w-3.5" />}
            {showMoreFilters ? 'Hide Filters' : 'More Filters'}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant={typeFilter === 'all' ? 'primary' : 'outline'} size="sm" onClick={() => setTypeFilter('all')}>All</Button>
          <Button variant={typeFilter === 'receipt' ? 'primary' : 'outline'} size="sm" onClick={() => setTypeFilter('receipt')}>Diary</Button>
          <Button variant={typeFilter === 'dispatch' ? 'primary' : 'outline'} size="sm" onClick={() => setTypeFilter('dispatch')}>Dispatch</Button>
          {isEfileEnabled && <Button variant={typeFilter === 'efile' ? 'primary' : 'outline'} size="sm" onClick={() => setTypeFilter('efile')}>e-File</Button>}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {['all', 'In Progress', 'Pending', 'Completed'].map((status) => (
            <Button
              key={status}
              size="sm"
              variant={statusFilter === (status === 'all' ? 'all' : status) ? 'primary' : 'outline'}
              onClick={() => setStatusFilter(status === 'all' ? 'all' : status)}
            >
              {status === 'all' ? 'All Status' : status}
            </Button>
          ))}
        </div>

        {showMoreFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3 mt-4">
            <Input label="Ref Type" value={columnFilters.referenceType} onChange={(e) => setColumnFilters(p => ({ ...p, referenceType: e.target.value }))} />
            <Input label="Diary/Disp No." value={columnFilters.dakNumber} onChange={(e) => setColumnFilters(p => ({ ...p, dakNumber: e.target.value }))} />
            <Input label="Date" value={columnFilters.date} onChange={(e) => setColumnFilters(p => ({ ...p, date: e.target.value }))} />
            <Input label="Action Required" value={columnFilters.actionRequired} onChange={(e) => setColumnFilters(p => ({ ...p, actionRequired: e.target.value }))} />
            <Input label="Assigned To" value={columnFilters.assignedTo} onChange={(e) => setColumnFilters(p => ({ ...p, assignedTo: e.target.value }))} />
            <Input label="Status" value={columnFilters.status} onChange={(e) => setColumnFilters(p => ({ ...p, status: e.target.value }))} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5 mb-6">
        {[
          { label: 'Total', value: complianceStats.total, icon: <FolderOpen className="w-5 h-5" />, tone: 'bg-indigo-50 text-indigo-700' },
          { label: 'In Progress', value: complianceStats.inProgress, icon: <Clock3 className="w-5 h-5" />, tone: 'bg-amber-50 text-amber-700' },
          { label: 'Completed', value: complianceStats.completed, icon: <CheckSquare className="w-5 h-5" />, tone: 'bg-emerald-50 text-emerald-700' },
          { label: 'Pending', value: complianceStats.pending, icon: <BellRing className="w-5 h-5" />, tone: 'bg-rose-50 text-rose-700' },
          { label: 'Updated This Week', value: complianceStats.thisWeek, icon: <MessageSquareText className="w-5 h-5" />, tone: 'bg-cyan-50 text-cyan-700' }
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className={`inline-flex rounded-lg p-2 ${item.tone}`}>{item.icon}</div>
            <div className="mt-3 text-2xl font-bold text-gray-900">{item.value}</div>
            <div className="text-sm text-gray-500">{item.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table 
          columns={columns} 
          data={filteredRows} 
          sortConfig={sortConfig}
          onSort={handleSort}
          tableClassName="table-fixed"
          density={tableDensity}
          enableColumnVisibility
          enableColumnResize
          showColumnControls
          onColumnVisibilityChange={setVisibleComplianceColumns}
          onColumnResizeChange={setTableComplianceColumnWidths}
        />
        <p className="px-4 pb-2 text-xs text-gray-500 lg:hidden">Tip: swipe horizontally to view all table columns.</p>
        
        <Pagination 
          currentPage={page} 
          totalPages={totalPages} 
          onPageChange={setPage} 
          totalItems={filteredRows.length}
          itemsPerPage={limit}
        />
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setShowFurtherCompliance(false);
        }} 
        title="Compliance Update"
        size="2xl"
      >
        <div className="space-y-5">
          <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0 flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-white border border-indigo-100 shadow-sm text-indigo-600 flex-shrink-0">
                  {String(selectedCompliance?.type || '') === 'dispatch' ? <Truck className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold px-2.5 py-1 bg-white border border-indigo-100 text-indigo-700 rounded-full uppercase tracking-wide">
                      {String(selectedCompliance?.referenceType || '-')}
                    </span>
                    {selectedCompliance?.status ? (
                      <span className="text-[11px] font-semibold px-2.5 py-1 bg-indigo-600 text-white rounded-full">
                        {selectedCompliance.status}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-2 text-base font-semibold text-gray-900 break-words [overflow-wrap:anywhere]" title={detail?.subject || selectedCompliance?.subject || selectedCompliance?.actionRequired || ''}>
                    {detail?.subject || selectedCompliance?.subject || selectedCompliance?.actionRequired || '-'}
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-white/70 rounded-lg px-2 py-1.5 border border-gray-200">
                      <Hash className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-800">
                        {detail?.diaryNumber || detail?.dispatchNumber || selectedCompliance?.dakNumber || `#${selectedCompliance?.id || '-'}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-white/70 rounded-lg px-2 py-1.5 border border-gray-200">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-800">
                        {formatDate(detail?.dateReceived || detail?.dateIssued || selectedCompliance?.createdAt || detail?.createdAt || null) || '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-white/70 rounded-lg px-2 py-1.5 border border-gray-200 min-w-0">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-gray-800 break-words [overflow-wrap:anywhere]" title={(detail?.sender || detail?.recipient || selectedCompliance?.sender || '-') || ''}>
                        {detail?.sender || detail?.recipient || selectedCompliance?.sender || '-'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-600">
                    Assigned: <span className="font-medium text-gray-800">{selectedCompliance?.assignedToUser?.name || '-'}</span>
                    {assignedDateValue ? (
                      <span className="ml-1 text-gray-500">({formatDate(assignedDateValue)})</span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={handleOpenCase}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                  disabled={!isEditable}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Case
                </Button>
                <Button
                  onClick={() => setShowFurtherCompliance(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                  disabled={!isEditable}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Further Compliance
                </Button>
                <Button
                  onClick={handleSaveDetails}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                  disabled={!isEditable}
                >
                  Save Details
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {['In Progress', 'Pending', 'Completed'].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleQuickStatusChange(status)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  (selectedCompliance?.status || 'In Progress') === status
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300 hover:text-indigo-700'
                }`}
              >
                Mark {status}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">Case Stage</div>
              <div className="mt-2 font-semibold text-gray-800">{selectedCompliance?.status || 'In Progress'}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">Assigned To</div>
              <div className="mt-2 font-semibold text-gray-800">{selectedCompliance?.assignedToUser?.name || '-'}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">Last Update</div>
              <div className="mt-2 font-semibold text-gray-800">{formatDate(assignedDateValue) || '-'}</div>
            </div>
          </div>

          {modalSteps.length > 1 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-2 sm:p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {modalSteps.map((s, idx) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setModalStep(idx)}
                    className={`w-full rounded-lg px-3 py-2.5 text-sm font-medium text-left transition-colors ${
                      idx === modalStep
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {detailLoading ? (
            <div className="text-sm text-gray-500">Loading details...</div>
          ) : (
            <>
              {showFurtherCompliance && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-amber-900">Further Compliance Record</h3>
                    <button
                      type="button"
                      onClick={() => setShowFurtherCompliance(false)}
                      className="text-xs font-medium text-amber-700 hover:text-amber-900"
                    >
                      Close
                    </button>
                  </div>
                  <div className="mb-3 rounded-lg border border-amber-200 bg-white/80 p-3 text-sm text-amber-900">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Return-from-office flow</div>
                    <div className="text-amber-800">This step is used when the file is returned from this office and being sent on to another officer.</div>
                  </div>

                  {latestComplianceHistory && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-white/80 p-3 text-sm text-amber-900">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Previous compliance</div>
                      <div className="font-medium">Officer: {latestComplianceHistory.officerName || '-'}</div>
                      <div className="text-amber-800">
                        Diary: {latestComplianceHistory.diaryNumber || '-'}
                        {latestComplianceHistory.diaryDate ? ` • ${formatDate(latestComplianceHistory.diaryDate)}` : ''}
                      </div>
                      {latestComplianceHistory.remarks && (
                        <div className="mt-1 text-amber-800">Remarks: {latestComplianceHistory.remarks}</div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">Officer / Office</label>
                      <SearchableCombobox
                        items={buildRecipientItems(detailRecipients)}
                        value={getSelectedFurtherOfficerValue()}
                        onChange={handleFurtherComplianceOfficerSelect}
                        placeholder={detailRecipients.length > 0 ? 'Search officer or office contact' : 'Type officer name manually'}
                      />
                    </div>
                    <Input
                      label="Or Type Officer Name"
                      value={editFields.officerName}
                      onChange={(e) => setEditFields(p => ({ ...p, officerName: e.target.value }))}
                      placeholder="Officer to whom file was returned"
                    />
                    <Input
                      label="Return Office Diary Number (Optional)"
                      value={editFields.officeDiaryNumber}
                      onChange={(e) => setEditFields(p => ({ ...p, officeDiaryNumber: e.target.value }))}
                      placeholder="e.g. GM/2026/101"
                    />
                    <Input
                      label="Return Office Diary Date (Optional)"
                      type="date"
                      value={editFields.officeDiaryDate}
                      onChange={(e) => setEditFields(p => ({ ...p, officeDiaryDate: e.target.value }))}
                    />
                    <div className="md:col-span-2">
                      <Textarea
                        label="Compliance Remarks"
                        rows="4"
                        value={editFields.comments}
                        onChange={(e) => setEditFields(p => ({ ...p, comments: e.target.value }))}
                        placeholder="Enter the compliance remarks and action carried out by the officer..."
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={handleFurtherCompliance}
                      className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                      disabled={!isEditable}
                    >
                      Save Further Compliance
                    </Button>
                  </div>
                </div>
              )}

              {selectedCompliance?.type === 'receipt' && modalStepKey === 'forward' && !showFurtherCompliance && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Forward To (Officer)</label>
                        <div className="flex items-center gap-2">
                          {(editFields.forwardedTo || editFields.forwardedToName || editFields.forwardedAt) && (
                            <button
                              type="button"
                              onClick={() => setEditFields(p => ({ ...p, forwardedTo: '', forwardedToName: '', forwardedAt: '' }))}
                              className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsAddContactOpen(true)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add New Contact
                          </button>
                        </div>
                      </div>
                      <SearchableCombobox
                        items={buildRecipientItems(detailRecipients)}
                        value={getSelectedRecipientValue(detailRecipients)}
                        onChange={handleRecipientChange}
                        placeholder={detailRecipients.length > 0 ? 'Select officer or contact' : 'Select officer from address book or office users'}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {detailRecipients.length > 0
                          ? 'Linked user selection stores User ID; contact selection stores officer name.'
                          : 'No saved contacts found. Add a new address book contact or type an officer name.'}
                      </p>
                    </div>

                    <div>
                      <Input
                        label="Or Type Officer Name"
                        value={editFields.forwardedToName}
                        onChange={(e) => setEditFields(p => ({ ...p, forwardedToName: e.target.value, forwardedTo: '' }))}
                        placeholder="Type officer name if not in list"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Input
                        label="Forward Date"
                        type="datetime-local"
                        value={editFields.forwardedAt || ''}
                        onChange={(e) => setEditFields(p => ({ ...p, forwardedAt: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {modalStepKey === 'comments' && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                  <div className="mb-2 text-xs sm:text-sm text-gray-500">
                    Case linking is managed in New Diary. Use this area for compliance comments and updates.
                  </div>
                  <Textarea
                    label="Remarks"
                    rows="8"
                    value={editFields.comments}
                    onChange={(e) => setEditFields(p => ({ ...p, comments: e.target.value }))}
                    placeholder="Type remarks for the history..."
                  />
                  <div className="mt-3">
                    <Button
                      onClick={handleSaveDetails}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                      disabled={!isEditable}
                    >
                      Save Remarks
                    </Button>
                  </div>
                </div>
              )}

              {!showFurtherCompliance && detailHistory.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:p-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-gray-900">Case History</h3>
                    <span className="text-xs font-medium text-gray-500">Same diary / same case</span>
                  </div>
                  <div className="space-y-3">
                    {detailHistory.map((entry) => (
                      <div key={entry.id} className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">
                            {entry.actionTaken || 'Compliance Update'}
                          </span>
                          <span className="text-[11px] text-gray-500">{formatDate(entry.updatedAt) || '-'}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-700">
                          <span className="font-medium text-gray-900">Officer:</span> {entry.officerName || '-'}
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="font-medium text-gray-900">Diary:</span> {entry.diaryNumber || '-'}
                          {entry.diaryDate ? ` • ${formatDate(entry.diaryDate)}` : ''}
                        </div>
                        {entry.remarks ? (
                          <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-gray-700">
                            {entry.remarks}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <AddressBookModal
        isOpen={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        onSuccess={handleContactAdded}
      />
    </>
  );
};

export default ComplianceList;
